import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import StepProgress from "./StepProgress";
import FlooringTypeStep from "./steps/FlooringTypeStep";
import SpaceDetailsStep from "./steps/SpaceDetailsStep";
import ContactInfoStep from "./steps/ContactInfoStep";
import SchedulingStep from "./steps/SchedulingStep";
import ConfirmationStep from "./steps/ConfirmationStep";
import {
  flooringTypeSchema,
  spaceDetailsSchema,
  contactInfoSchema,
  schedulingSchema,
  FlooringTypeData,
  SpaceDetailsData,
  ContactInfoData,
  SchedulingData,
} from "@/lib/validation";
import { useToast } from "@/hooks/use-toast";

// ============================================================
// N8N WEBHOOK CONFIGURATION
// Replace with your actual N8N webhook URL when ready
// ============================================================
const N8N_WEBHOOK_URL = ""; // TODO: Add your N8N webhook URL here

interface WebhookPayload {
  contactInfo: ContactInfoData;
  projectDetails: {
    flooringTypes: string[];
    approximateSize: string;
    rooms: string[];
    currentFlooring: string;
  };
  scheduling: {
    preferredDate: string;
    preferredTime: string;
    additionalNotes: string;
  };
  submittedAt: string;
}

const STEPS = ["Flooring Type", "Space Details", "Contact Info", "Scheduling"];

interface FormData {
  flooringType: FlooringTypeData;
  spaceDetails: SpaceDetailsData;
  contactInfo: ContactInfoData;
  scheduling: SchedulingData;
}

const initialFormData: FormData = {
  flooringType: { flooringTypes: [] },
  spaceDetails: {
    approximateSize: "",
    rooms: [],
    currentFlooring: "Other",
  },
  contactInfo: {
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zipCode: "",
  },
  scheduling: {
    preferredDate: undefined as unknown as Date,
    preferredTime: undefined as unknown as SchedulingData["preferredTime"],
    additionalNotes: "",
  },
};

const EstimateWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const { toast } = useToast();

  const validateCurrentStep = (): boolean => {
    let result;
    setErrors({});

    switch (currentStep) {
      case 1:
        result = flooringTypeSchema.safeParse(formData.flooringType);
        break;
      case 2:
        result = spaceDetailsSchema.safeParse(formData.spaceDetails);
        break;
      case 3:
        result = contactInfoSchema.safeParse(formData.contactInfo);
        break;
      case 4:
        result = schedulingSchema.safeParse(formData.scheduling);
        break;
      default:
        return true;
    }

    if (!result.success) {
      const fieldErrors: Record<string, string[]> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        if (!fieldErrors[field]) {
          fieldErrors[field] = [];
        }
        fieldErrors[field].push(err.message);
      });
      setErrors(fieldErrors);
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < STEPS.length) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  // Submit form data to N8N webhook
  const submitToWebhook = async (data: FormData): Promise<boolean> => {
    // If webhook URL is not configured, allow submission to proceed (dev mode)
    if (!N8N_WEBHOOK_URL) {
      console.warn("N8N Webhook URL not configured - form data not sent to external service");
      return true;
    }

    const payload: WebhookPayload = {
      contactInfo: data.contactInfo,
      projectDetails: {
        flooringTypes: data.flooringType.flooringTypes,
        approximateSize: data.spaceDetails.approximateSize,
        rooms: data.spaceDetails.rooms,
        currentFlooring: data.spaceDetails.currentFlooring,
      },
      scheduling: {
        preferredDate: data.scheduling.preferredDate?.toISOString() || "",
        preferredTime: data.scheduling.preferredTime || "",
        additionalNotes: data.scheduling.additionalNotes || "",
      },
      submittedAt: new Date().toISOString(),
    };

    try {
      await fetch(N8N_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "no-cors", // N8N webhooks typically require no-cors
        body: JSON.stringify(payload),
      });
      return true;
    } catch (error) {
      console.error("Failed to submit to webhook:", error);
      return false;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Submit to N8N webhook
    const webhookSuccess = await submitToWebhook(formData);
    
    if (!webhookSuccess) {
      toast({
        title: "Submission Error",
        description: "There was an issue submitting your request. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }
    
    setIsSubmitting(false);
    setIsComplete(true);
    
    toast({
      title: "Estimate Request Submitted!",
      description: "We'll contact you within 24 hours.",
    });
  };

  if (isComplete) {
    return <ConfirmationStep />;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <StepProgress
        currentStep={currentStep}
        totalSteps={STEPS.length}
        steps={STEPS}
      />

      <div className="bg-background rounded-2xl border border-border p-6 md:p-10 shadow-sm">
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <FlooringTypeStep
              key="step1"
              data={formData.flooringType}
              onDataChange={(data) =>
                setFormData({ ...formData, flooringType: data })
              }
              errors={errors.flooringTypes}
            />
          )}
          {currentStep === 2 && (
            <SpaceDetailsStep
              key="step2"
              data={formData.spaceDetails}
              onDataChange={(data) =>
                setFormData({ ...formData, spaceDetails: data })
              }
              errors={errors}
            />
          )}
          {currentStep === 3 && (
            <ContactInfoStep
              key="step3"
              data={formData.contactInfo}
              onDataChange={(data) =>
                setFormData({ ...formData, contactInfo: data })
              }
              errors={errors}
            />
          )}
          {currentStep === 4 && (
            <SchedulingStep
              key="step4"
              data={formData.scheduling}
              onDataChange={(data) =>
                setFormData({ ...formData, scheduling: data })
              }
              errors={errors}
            />
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-10 pt-6 border-t border-border">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>

          <Button
            variant="coral"
            onClick={handleNext}
            disabled={isSubmitting}
            className="gap-2 min-w-[140px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : currentStep === STEPS.length ? (
              "Submit Request"
            ) : (
              <>
                Next
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EstimateWizard;
